import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, ExternalLink, Trash2, AlertTriangle, Calculator, MapPin, Calendar, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  DRAFT: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
  SIMULATED: { label: 'Simulee', color: 'bg-blue-100 text-blue-700' },
  DOCUMENTS_GENERATED: { label: 'Documents', color: 'bg-cyan-100 text-cyan-700' },
  SIGNATURE_PENDING: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  SIGNED: { label: 'Signee', color: 'bg-emerald-100 text-emerald-700' },
  SIGNED_WAITING_PAYMENT: { label: 'Att. paiement', color: 'bg-orange-100 text-orange-700' },
  PAID_CONFIRMED: { label: 'Payee', color: 'bg-green-100 text-green-700' },
  ACTIVE: { label: 'Active', color: 'bg-green-200 text-green-800' },
};

export default function MemberSimulationsModal({ open, onClose, membre, token }) {
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const email = membre?.email || membre?.raison_sociale || '';

  const filteredSimulations = useMemo(() => {
    if (!searchQuery.trim()) return simulations;
    const q = searchQuery.trim().toLowerCase();
    return simulations.filter(sim => {
      const nom = (sim.nom || '').toLowerCase();
      const ref = (sim.reference || '').toLowerCase();
      const montant = sim.contribution_ht != null ? sim.contribution_ht.toLocaleString('fr-FR') : '';
      return nom.includes(q) || ref.includes(q) || montant.includes(q);
    });
  }, [simulations, searchQuery]);

  const fetchSims = useCallback(async () => {
    if (!email || !token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/domium/subscribe/member-simulations/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSimulations(res.data.simulations || []);
    } catch {
      toast.error('Erreur chargement simulations');
    } finally { setLoading(false); }
  }, [email, token]);

  useEffect(() => { if (open) fetchSims(); }, [open, fetchSims]);

  const handleDelete = async (ref) => {
    if (!window.confirm(`Supprimer la simulation ${ref} ?`)) return;
    setDeleting(ref);
    try {
      await axios.delete(`${API}/api/domium/subscribe/${ref}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Simulation ${ref} supprimee`);
      setSimulations(prev => prev.filter(s => s.reference !== ref));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    } finally { setDeleting(null); }
  };

  const handleResume = (ref) => {
    window.open(`/simulateur-dos?resume=${ref}`, '_blank');
    onClose();
  };

  const getStatus = (s) => STATUS_LABELS[s] || { label: s, color: 'bg-gray-100 text-gray-600' };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-3 border-b">
          <DialogTitle className="flex items-center gap-3 text-base">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
              <Calculator className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-tight">Simulations DOS DOMIUM</p>
              <p className="text-xs text-gray-500 truncate" title={membre?.raison_sociale || `${membre?.prenom || ''} ${membre?.nom || ''}`}>
                {membre?.raison_sociale || `${membre?.prenom || ''} ${membre?.nom || ''}`.trim() || '-'}
              </p>
            </div>
          </DialogTitle>
          {simulations.length > 0 && (
            <div className="relative mt-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, référence ou montant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 pl-8 text-xs"
                data-testid="modal-sim-search-input"
              />
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2.5 py-3 pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            </div>
          ) : simulations.length === 0 ? (
            <Alert className="bg-gray-50">
              <AlertTriangle className="h-4 w-4 text-gray-400" />
              <AlertDescription className="text-gray-500">Aucune simulation trouvée pour ce membre.</AlertDescription>
            </Alert>
          ) : filteredSimulations.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">Aucun résultat pour "{searchQuery}"</p>
          ) : (
            filteredSimulations.map((sim) => {
              const st = getStatus(sim.status);
              return (
                <div
                  key={sim.reference}
                  className="p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all duration-200 bg-white border-l-4 border-l-emerald-400"
                  data-testid={`sim-row-${sim.reference}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Référence + statut */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          {sim.reference}
                        </span>
                        <Badge className={`text-xs px-2 py-0.5 font-semibold ${st.color}`}>{st.label}</Badge>
                      </div>

                      {/* Nom — entièrement visible */}
                      <p
                        className="text-sm font-medium text-gray-900 leading-snug break-words"
                        title={sim.nom}
                        data-testid={`sim-name-${sim.reference}`}
                      >
                        {sim.nom || '—'}
                      </p>

                      {/* Métadonnées en grille fluide */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
                        {sim.contribution_ht > 0 && (
                          <span className="inline-flex items-center gap-1 font-semibold text-gray-900">
                            <span className="text-gray-400 font-normal">Contribution :</span>
                            {sim.contribution_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                          </span>
                        )}
                        {sim.territoire && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-gray-400" />
                            <span>{sim.territoire}</span>
                          </span>
                        )}
                        {sim.created_at && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            <span>{new Date(sim.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions empilées verticalement pour cohérence visuelle */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 font-medium"
                        onClick={() => handleResume(sim.reference)}
                        data-testid={`resume-sim-${sim.reference}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Reprendre
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                        onClick={() => handleDelete(sim.reference)}
                        disabled={deleting === sim.reference}
                        data-testid={`delete-sim-${sim.reference}`}
                      >
                        {deleting === sim.reference ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t pt-3 flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">
            {searchQuery ? `${filteredSimulations.length} / ${simulations.length}` : simulations.length} simulation(s)
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
